import React, { useEffect } from "react";
import { usePayment } from "../contexts/PaymentContext";
import { useCart } from "../contexts/CartContext";
import { cancelAll, createPrintfulOrder, sendEmailConfirmation } from "../api";
// Not in the legacy `src/api.ts` barrel `../api` resolves to — see api/index.ts.
import { saveCustomerCard } from "../api/customer";
import { sendAdminAlert } from "../api/admin";
import { useNavigate } from "react-router-dom";
import { Order, Payment } from "square";
import PaymentTimer from "../components/PaymentTimer";
import { ShippingRateItem } from "../types/ShippingRate";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { getVariationSku } from "../types/OrderItem";
import { trackPurchase } from "../lib/meta-pixel";

interface PaymentConfirmProps {
  order: Order;
  payment: Payment;
  contactInfo: Payment;
  phone: string;
  selectedShipping: ShippingRateItem;
  /** The buyer ticked "Save my information for a faster checkout" while paying
   *  with a newly entered card. Resolved by the checkout page (a wallet token or
   *  an existing card on file never sets it). */
  saveCard?: boolean;
  /**
   * A message about the buyer's ACCOUNT (not the order) to carry through to the
   * receipt page — currently only the involuntary mid-checkout sign-out.
   *
   * It has to travel because the checkout page can't deliver it: this modal
   * opens over that page immediately and then navigates away, so a notice left
   * behind there is seen only if the order FAILS, which is the one case where
   * it doesn't apply. The receipt is where the buyer actually ends up.
   */
  accountNotice?: string;
  /** Called when placing the order fails so the parent can close this modal.
   *  Without it a failure left the timer spinning forever with no way out. */
  onFailure?: () => void;
}

const PaymentConfirm: React.FC<PaymentConfirmProps> = ({
  order,
  payment,
  contactInfo,
  phone,
  selectedShipping,
  saveCard,
  accountNotice,
  onFailure,
}) => {
  const { setErrorMessage } = usePayment();
  const { cart, setIsCartOpen, resetCart } = useCart();
  const { session } = useCustomerAuth();
  const navigate = useNavigate();

  // Place the order WITHOUT capturing the card. The payment stays AUTHORIZED
  // (no Square processing fee) through the free-cancellation window; the
  // scheduled finalize-orders job captures it and confirms the Printful draft
  // into production after the window. A cancellation before then is a free void.
  const handlePlaceOrder = async () => {
    const purchasedCart = cart;

    // 1. Create the Printful order as a DRAFT (confirm:false). It's linked to the
    //    Square order by external_id; finalize-orders confirms it later. Doing
    //    this now (pre-capture) means a setup failure costs nothing — we void the
    //    authorized payment instead of refunding a captured one.
    let printfulResponse;
    try {
      printfulResponse = await createPrintfulOrder(
        purchasedCart,
        contactInfo,
        phone,
        selectedShipping,
        order.id,
        session?.authToken,
      );
    } catch (draftErr) {
      printfulResponse = { error: String(draftErr) };
    }
    if (!printfulResponse?.data || !!printfulResponse?.error) {
      // Draft setup failed — void the authorized payment (no fee, no charge).
      void sendAdminAlert(
        `⚠️ Printful Draft Setup Failed — ${order.id}`,
        "Creating the Printful draft at checkout failed; the authorized payment is being voided so the customer is not charged.",
        {
          "Order ID": order.id,
          "Printful Error": String(
            (printfulResponse as { detail?: string })?.detail ??
              printfulResponse?.error ??
              "unknown",
          ),
        },
      );
      try {
        // suppressCancellationEmail: the customer never saw this order
        // confirmed, so a "Your Order Has Been Canceled" email out of nowhere
        // is pure confusion (it's what the Apple Pay support report quoted).
        // The on-screen message below is the correct surface.
        await cancelAll(
          payment.orderId!,
          undefined,
          session?.authToken,
          contactInfo.buyerEmailAddress,
          { suppressCancellationEmail: true },
        );
      } catch {
        // Void failed too — cleanup-orders will reconcile; surface the error.
      }
      // Surface address validation errors so the customer knows what to fix
      // (Printful returns a human-readable "Recipient: ..." message for these).
      // All other Printful failures stay generic so internal detail isn't leaked.
      const printfulDetail =
        (printfulResponse as { detail?: string })?.detail ?? "";
      const isAddressError =
        /shipping address|recipient|zip|postal|state|city/i.test(
          printfulDetail,
        );
      setErrorMessage(
        isAddressError
          ? `Your shipping address couldn't be verified: ${printfulDetail} Please go back and correct it.`
          : "We couldn't set up your order with our fulfillment partner. You have NOT been charged. Please try again, or contact support@americanaandcompany.com.",
      );
      // Close the modal — leaving it open meant an infinite spinner.
      onFailure?.();
      return;
    }

    // 2. Send the immediate "order received" email (best-effort). The card is
    //    charged later at finalize; the next email after this is "shipped".
    //    Build the receipt breakdown from the authoritative Square order so the
    //    emailed subtotal/discount/shipping/tax reconcile exactly with the total
    //    (otherwise the customer sees an item price and a larger, unexplained total).
    try {
      const money = (m?: { amount?: unknown } | null) =>
        Number((m?.amount as number | bigint | undefined) ?? 0);
      const donationItem = order.lineItems?.find(
        (li) => li.name === "Donation",
      );
      const donation = money(
        donationItem?.totalMoney ?? donationItem?.basePriceMoney,
      );
      const discount = money(order.totalDiscountMoney);
      const shipping = money(order.totalServiceChargeMoney);
      const tax = money(order.totalTaxMoney);
      const total = money(order.totalMoney);
      // Product subtotal (pre-discount) = total unwound of the other components.
      const subtotal = total + discount - tax - shipping - donation;

      const emailResult = await sendEmailConfirmation(
        order.id!,
        total,
        purchasedCart,
        contactInfo,
        {
          minDeliveryDate: selectedShipping?.minDeliveryDate,
          maxDeliveryDate: selectedShipping?.maxDeliveryDate,
        },
        {
          subtotal,
          discount,
          promoLabel: order.discounts?.[0]?.name ?? undefined,
          donation,
          shipping,
          tax,
          total,
        },
      );
      // sendEmailConfirmation returns null on its own guard clauses (no email
      // address, no items, zero total) and {error} on a failed send — neither
      // throws. Without this branch a confirmation that never went out looked
      // identical to one that did, from here AND from the buyer's side, which
      // is exactly how "I paid and never got an email" reached us as a mystery
      // instead of an alert. The order is already placed, so this must never
      // become a customer-visible failure — just make the silence audible.
      if (!emailResult?.data || emailResult?.error) {
        console.error("Order-received email not sent:", emailResult?.error);
        void sendAdminAlert(
          `⚠️ Order Confirmation Email Not Sent — ${order.id}`,
          "The order was placed successfully but the 'order received' email did not send. The customer has no confirmation — reach out to them.",
          {
            "Order ID": order.id,
            "Buyer Email": contactInfo.buyerEmailAddress ?? "(none on order)",
            Error: String(
              emailResult?.error ?? "no response from send-email-order",
            ),
          },
        );
      }
    } catch (emailErr) {
      console.error("Order-received email failed (non-fatal):", emailErr);
      void sendAdminAlert(
        `⚠️ Order Confirmation Email Threw — ${order.id}`,
        "The order was placed successfully but sending the 'order received' email threw. The customer has no confirmation — reach out to them.",
        {
          "Order ID": order.id,
          "Buyer Email": contactInfo.buyerEmailAddress ?? "(none on order)",
          Error: String((emailErr as Error)?.message ?? emailErr),
        },
      );
    }

    // 3. Store the card on file, if the buyer asked us to. Deliberately here
    //    rather than at authorization: the card is only remembered once the order
    //    is actually placed, so abandoning the confirm modal leaves nothing
    //    behind. Fire-and-forget — the sale is already done, so a failed save
    //    must never become a checkout error the customer sees. Square accepts the
    //    payment id as the CreateCard source while the payment is AUTHORIZED
    //    (24-hour window), which is exactly where it sits until finalize-orders
    //    captures it.
    if (saveCard && session?.authToken && payment.id) {
      void saveCustomerCard(session.authToken, payment.id).then((res) => {
        if (!res?.data || res.error)
          console.error("Saving card failed (non-fatal):", res?.error);
      });
    }

    // 4. Meta pixel Purchase — the event ad attribution keys off. Reported here
    //    (order placed) rather than on the receipt page so a reloaded or
    //    back-navigated receipt can't double-count. eventID is the Square order
    //    id, so a future Conversions API send for the same order dedupes.
    //    Value is the authoritative Square total, not the cart subtotal.
    trackPurchase({
      orderId: order.id!,
      skus: purchasedCart.map((i) => getVariationSku(i.variation)),
      totalCents: Number(order.totalMoney?.amount ?? 0),
      numItems: purchasedCart.reduce((n, i) => n + i.quantity, 0),
    });

    // 5. Clear cart and go to the receipt page (authorized payment as receipt).
    resetCart();
    navigate("/payment/receipt", {
      state: {
        receipt: payment,
        purchasedCart,
        deliveryEstimate: selectedShipping
          ? {
              minDeliveryDate: selectedShipping.minDeliveryDate,
              maxDeliveryDate: selectedShipping.maxDeliveryDate,
            }
          : undefined,
        accountNotice,
        // So the receipt can name the inbox the confirmation went to.
        confirmationEmail: contactInfo.buyerEmailAddress ?? undefined,
      },
      replace: true,
    });
  };

  const handleCancel = async () => {
    try {
      await cancelAll(
        payment.orderId!,
        undefined,
        session?.authToken,
        contactInfo.buyerEmailAddress,
      );
    } catch (error: any) {
      setErrorMessage(
        error?.message ??
          "Could not cancel the order. Please contact support@americanaandcompany.com.",
      );
      // Close the modal so the customer isn't stranded with a dead timer;
      // the error toast stays visible on the checkout page.
      onFailure?.();
      return;
    }
    navigate("/home", { replace: true });
    setIsCartOpen(true);
  };

  // Guard: if payment is null, redirect rather than calling setState during render.
  useEffect(() => {
    if (payment == null) {
      console.error("PaymentConfirm rendered with no payment.");
      setErrorMessage("There is no payment to be made.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (payment == null) {
    return <></>;
  }

  return (
    <div className="payment-confirm-content">
      {/* Card Icon */}
      <div className="payment-confirm-icon-wrapper">
        <svg
          className="payment-confirm-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      </div>

      {/* Heading */}
      <div className="flex flex-col items-center gap-1">
        <h2 className="payment-confirm-title">Placing Your Order</h2>
        <p className="payment-confirm-subtitle">
          We're placing your order. Please don't close this window.
        </p>
      </div>

      {/* Countdown Timer */}
      <PaymentTimer
        seconds={5}
        onComplete={handlePlaceOrder}
        onCancel={handleCancel}
      />

      <p className="payment-confirm-note">
        Your order will be placed automatically when the timer completes. You
        won't be charged until we begin preparing it — you can cancel for free
        until then.
      </p>
    </div>
  );
};

export default PaymentConfirm;
